import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { login } from '../store/auth.slice'
import { useAppDispatch, useAppSelector } from '../hooks'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { Logo } from '../assets/logo'
import { LoginDto } from '../utils/types/auth'

// TODO fix login self-redirect
// TODO login persist queries on redirect

export const LoginView = () => {
  const [user, setUser] = useState<LoginDto>({ email: '', password: '' })
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ error: '' })
  const inputRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectPath = searchParams.get('to')
  const defaultEmail = searchParams.get('addr')

  useEffect(() => {
    if (authUser) {
      navigate('/dashboard', { viewTransition: true })
    }
    // eslint-disable-next-line
  }, [authUser])

  useEffect(() => {
    defaultEmail && setUser({ ...user, email: defaultEmail })
    // eslint-disable-next-line
  }, [defaultEmail])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRequestState({ error: '' })
    setUser({ ...user, [e.target.id]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      setIsLoading(true)

      const { status } = await dispatch(login(user)).unwrap()
      if (status === 200) {
        navigate(redirectPath || '/dashboard', {
          replace: true,
          viewTransition: true
        })
      }
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403) {
        navigate(`/confirm-email?addr=${user.email}`, { viewTransition: true })
      } else {
        setRequestState({
          error:
            errorObj.status === 401
              ? 'Incorrect email or password'
              : (errorObj.message ?? 'Error encountered')
        })
        setIsLoading(false)
      }
    }
  }

  const handleForgetPassword = () => {
    const query = user.email ? `?addr=${user.email}` : ''
    navigate(`/recover-account${query}`, { viewTransition: true })
  }

  return (
    <>
      {isLoading ? (
        <Spinner size='full' />
      ) : (
        <main>
          <div className='main_content login_content'>
            <Logo
              handler={() =>
                navigate(authUser ? '/dashboard' : '/', {
                  viewTransition: true
                })
              }
            />
            {requestState.error && (
              <Status
                error={requestState.error}
                reset={() => setRequestState({ ...requestState, error: '' })}
              />
            )}
            {redirectPath && <Status warning='Please log in' />}
            <form onSubmit={handleSubmit}>
              <label htmlFor='email'>Email:</label>
              <input
                id='email'
                type='email'
                value={user.email}
                onChange={handleChange}
                required
                autoFocus={!defaultEmail}
              />
              <label htmlFor='password'>Password:</label>
              <div className='input_container'>
                <input
                  id='password'
                  type={`${isPasswordVisible ? 'text' : 'password'}`}
                  value={user.password}
                  onChange={handleChange}
                  required
                  autoFocus={!!defaultEmail}
                  ref={inputRef}
                />
                <span
                  className='material-symbols-rounded'
                  onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
                  {isPasswordVisible ? 'visibility_off' : 'visibility'}
                </span>
              </div>
              <button className='submit'>Log in</button>
            </form>
            <div>
              <span>
                Don't have an account?{' '}
                <em
                  className='span-a'
                  onClick={() =>
                    navigate(`/sign-up?addr=${user.email}`, {
                      viewTransition: true
                    })
                  }>
                  Sign up now
                </em>
              </span>
              <hr />
              <span className='span-a' onClick={handleForgetPassword}>
                Forgot password
              </span>
            </div>
          </div>
        </main>
      )}
    </>
  )
}
